import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { HOME } from "@/constants/testIds";

const NAV_LINKS = [
  { label: "About", href: "#about", id: HOME.navLinkAbout },
  { label: "Services", href: "#services", id: HOME.navLinkServices },
  { label: "Industries", href: "#industries", id: HOME.navLinkIndustries },
  { label: "Team", href: "#team", id: HOME.navLinkTeam },
  { label: "Contact", href: "#contact", id: HOME.navLinkContact },
];

const Logo = () => (
  <img
    src="https://customer-assets-39nsmqrw.emergentagent.net/job_snv-jobs/artifacts/1yzrw5yj_Blue%20%26%20Pink.png"
    alt="SNV Elite Solutions"
    className="h-12 md:h-14 w-auto"
    width="180"
    height="112"
    decoding="async"
  />
);

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleAnchor = (e, href) => {
    if (!onHome) return;
    if (href.startsWith("#")) {
      e.preventDefault();
      const el = document.querySelector(href);
      if (el) {
        window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 80, behavior: "smooth" });
      }
      setOpen(false);
    }
  };

  return (
    <motion.header
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 bg-white transition-shadow ${
        scrolled ? "shadow-[0_4px_16px_rgba(15,23,42,0.06)]" : ""
      }`}
    >
      <div className="px-5 md:px-10 lg:px-16 h-20 md:h-24 flex items-center justify-between">
        <Link to="/" data-testid={HOME.navLogo} aria-label="SNV Elite Solutions home">
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={onHome ? l.href : `/${l.href}`}
              data-testid={l.id}
              onClick={(e) => handleAnchor(e, l.href)}
              className="font-display font-medium text-sm text-brand-ink hover:text-brand-blue transition-colors link-underline"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={onHome ? "#post-job" : "/#post-job"}
            data-testid={HOME.navCtaPost}
            onClick={(e) => handleAnchor(e, "#post-job")}
            className="font-display font-semibold text-sm px-5 py-3 bg-brand-blue text-white rounded-md hover:bg-brand-blue-dark transition-colors"
          >
            Post a Job
          </a>
          <a
            href={onHome ? "#submit-resume" : "/#submit-resume"}
            data-testid={HOME.navCtaResume}
            onClick={(e) => handleAnchor(e, "#submit-resume")}
            className="font-display font-semibold text-sm px-5 py-3 bg-brand-red text-white rounded-md hover:bg-brand-red-dark transition-colors"
          >
            Submit Resume
          </a>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-md border border-brand-border text-brand-ink"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle navigation"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden border-t border-brand-border bg-white"
          >
            <div className="px-5 py-6 flex flex-col gap-4">
              {NAV_LINKS.map((l) => (
                <a
                  key={l.href}
                  href={onHome ? l.href : `/${l.href}`}
                  onClick={(e) => handleAnchor(e, l.href)}
                  className="font-display font-medium text-brand-ink"
                >
                  {l.label}
                </a>
              ))}
              <div className="flex gap-3 pt-2">
                <a
                  href={onHome ? "#post-job" : "/#post-job"}
                  onClick={(e) => handleAnchor(e, "#post-job")}
                  className="font-display font-semibold text-sm px-4 py-3 bg-brand-blue text-white rounded-md flex-1 text-center"
                >
                  Post a Job
                </a>
                <a
                  href={onHome ? "#submit-resume" : "/#submit-resume"}
                  onClick={(e) => handleAnchor(e, "#submit-resume")}
                  className="font-display font-semibold text-sm px-4 py-3 bg-brand-red text-white rounded-md flex-1 text-center"
                >
                  Submit Resume
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
